/**
 * Merkflow Sidebar — floating dock ported from Kora.
 *
 * Kora reference:
 *   /Users/nielsvanharen/development/mijnrechtzaak/template/Kora-Main-File/Template/src/components/Sidebar.tsx
 *
 * Layout decisions for Merkflow:
 *   - Single flat nav (no nested submenus like Kora has for its component lib)
 *   - MAIN / CONTENT / SETTINGS / ADMIN section groups
 *   - w-[220px] expanded, w-[68px] collapsed
 *   - Floating card inset (`m-4 rounded-[2rem]`) with solid bg + hairline border
 *   - Orange gradient active pill
 *   - User mini-card pinned bottom
 *   - No workspace switcher here — lives in the topbar
 *
 * The nav tree + filter helper live in `sidebarNav.ts`; the section
 * header + nav row render blocks live in `SidebarSectionHeader.tsx` +
 * `SidebarNavRow.tsx`. `filterNavItems` + `NavItem` are re-exported here
 * for back-compat with existing importers (Sidebar.test.tsx).
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SidebarLogo } from '../ui/SidebarLogo';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { SidebarGroup } from './SidebarGroup';
import { SidebarSectionHeader } from './SidebarSectionHeader';
import { SidebarNavRow } from './SidebarNavRow';
import { COLLAPSIBLE_GROUPS, NAV, filterNavItems, type BadgeKey, type NavItem } from './sidebarNav';
import { useMe, useReviewPosts, useBillingStatus } from '@/hooks';
import { useWorkspaceContext, useIsOwner } from '../WorkspaceProvider';
import {
  adminFeedbackApi,
  adminWaitlistApi,
  ENGAGEMENT_COUNTS_INVALIDATE_EVENT,
  engagementApi,
} from '@/lib/api';
import { useAdminConfig } from '@/components/AdminConfigProvider';

export { filterNavItems, type NavItem } from './sidebarNav';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const COLLAPSED_KEY = 'merkflow-sidebar-collapsed';

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // Latest onClose in a ref so the close-on-nav effect keys on pathname only,
  // not on the (possibly inline) callback's identity.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { activeWorkspace } = useWorkspaceContext();
  const { data: user } = useMe();
  const { data: reviewPosts = [] } = useReviewPosts(activeWorkspace?.id ?? '');
  const reviewCount = reviewPosts.length;

  // Engagement unread poll (60s) + event-driven invalidation from the
  // thread inbox's optimistic mark-as-read (Phase 12). The `dispatchEngagementCountsInvalidate`
  // helper fires a window event; we catch it here and re-tick immediately
  // so the badge doesn't lag the UI by up to a minute.
  const [engagementUnread, setEngagementUnread] = useState(0);
  useEffect(() => {
    if (!activeWorkspace?.id) return;
    const tick = () => {
      engagementApi
        .getCounts(activeWorkspace.id)
        .then((c) => setEngagementUnread(c.total))
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 60_000);
    const onInvalidate = () => tick();
    window.addEventListener(ENGAGEMENT_COUNTS_INVALIDATE_EVENT, onInvalidate);
    return () => {
      clearInterval(t);
      window.removeEventListener(ENGAGEMENT_COUNTS_INVALIDATE_EVENT, onInvalidate);
    };
  }, [activeWorkspace?.id]);

  // Collapsed state (desktop only, persisted)
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setCollapsed(localStorage.getItem(COLLAPSED_KEY) === 'true');
  }, []);
  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem(COLLAPSED_KEY, String(next));
      }
      return next;
    });
  };

  // Close mobile drawer on navigation
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches) {
      onCloseRef.current();
    }
  }, [pathname]);

  const isAdmin = user?.systemRole === 'admin';
  const isOwner = useIsOwner();
  const { mockEnabled } = useAdminConfig();
  const isActive = (item: NavItem) =>
    item.exact ? pathname === item.to : pathname.startsWith(item.to);

  // V115/V116 — admin badge counters (waitlist + feedback). 60s
  // poll cadence; both queries gated on `isAdmin` so non-admin
  // members never trigger the requests.
  const { data: waitlistCount = { count: 0 } } = useQuery({
    queryKey: ['admin-waitlist-pending-count'],
    queryFn: adminWaitlistApi.pendingCount,
    refetchInterval: 60_000,
    enabled: isAdmin,
  });
  const { data: feedbackCount = { count: 0 } } = useQuery({
    queryKey: ['admin-feedback-new-count'],
    queryFn: adminFeedbackApi.newCount,
    refetchInterval: 60_000,
    enabled: isAdmin,
  });

  const badgeFor = (key?: BadgeKey) =>
    key === 'review'
      ? reviewCount
      : key === 'engagement'
        ? engagementUnread
        : key === 'waitlist'
          ? waitlistCount.count
          : key === 'feedback'
            ? feedbackCount.count
            : 0;
  // Plan-tiered UX (BRIEF-plan-tiered-ux PR1) — Starter sees a
  // simplified sidebar without Calendar / Discover / Storylines /
  // Blog / Analytics / Ads / Usage / AI Visibility. Trial =
  // Pro-level (per V81 plan_tier_features) so trialing customers
  // can evaluate every feature; the `isStarter` check excludes
  // them. Paused workspaces fall through to the same restricted
  // sidebar — they're effectively read-only and the omitted
  // features wouldn't work anyway.
  const { data: billing } = useBillingStatus(activeWorkspace?.id ?? '');
  // Platform admins (system_role=admin) bypass the Starter/paused gate
  // so they can see every menu item regardless of which workspace's
  // billing they're impersonating — same shape as the
  // regenerate-defaults 5/day admin bypass on GEO probes.
  const isStarter = !isAdmin && (billing?.plan === 'starter' || billing?.plan === 'paused');

  // Hide owner-only items from MEMBERs entirely. The matching routes are
  // also wrapped in OwnerOnlyRoute so a member who pastes the URL is bounced.
  // proOnly items are dropped for Starter and paused workspaces.
  const visibleItems = (items: NavItem[]) =>
    filterNavItems(items, { isOwner, mockEnabled, isStarter });

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        // Mobile (< md): a fixed drawer with translate-x animation.
        // Desktop (md+): a static flex sibling of <main> inside the
        // h-screen overflow-hidden row. The previous `md:sticky md:top-4`
        // assumed document scroll; under the new layout the parent doesn't
        // scroll so sticky has no anchor. Margin-based inset gives the same
        // 16px gap visually while keeping the sidebar a normal flex item.
        // `flex-shrink-0` prevents the floating dock from getting squeezed
        // when the main column has wide content.
        className={`fixed inset-y-4 left-4 z-50 flex flex-col overflow-y-auto rounded-[2rem] border border-[#e5e2db] bg-white transition-[width,transform] duration-200 md:static md:my-4 md:ml-4 md:flex md:h-[calc(100vh-2rem)] md:flex-shrink-0 dark:border-white/[0.08] dark:bg-[#13131c] ${isOpen ? 'translate-x-0' : '-translate-x-[110%] md:translate-x-0'} ${collapsed ? 'w-[220px] md:w-[68px]' : 'w-[220px]'} `}
      >
        {/* -------- Logo row -------- */}
        <div
          className={`flex items-center pt-6 pb-2 ${
            collapsed ? 'justify-center px-2' : 'justify-between px-5'
          }`}
        >
          <SidebarLogo collapsed={collapsed} />
          <button
            type="button"
            onClick={onClose}
            className="text-text-tertiary hover:text-text-primary md:hidden"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* -------- Workspace switcher -------- */}
        <WorkspaceSwitcher collapsed={collapsed} />

        {/* -------- Nav sections -------- */}
        {/*
          Main / Content / Growth stay flat (rendered via SectionHeader).
          Settings and Admin are collapsible — wrapped in SidebarGroup so the
          user can fold them away. Route-prefix match auto-opens the group
          on direct navigation (e.g. /admin/plans reveals Admin).
        */}
        <nav className="flex-1 pt-2">
          {NAV.filter((s) => !s.adminOnly || isAdmin).map((section, sIdx) => {
            const items = visibleItems(section.items);
            if (items.length === 0) return null;
            const list = (
              <ul className="space-y-0.5 px-2">
                {items.map((item) => (
                  <SidebarNavRow
                    key={item.to}
                    item={item}
                    active={isActive(item)}
                    collapsed={collapsed}
                    badge={badgeFor(item.badge)}
                  />
                ))}
              </ul>
            );
            const groupConfig = COLLAPSIBLE_GROUPS[section.label];
            if (groupConfig) {
              return (
                <SidebarGroup
                  key={section.label}
                  label={section.i18nKey ? t(section.i18nKey) : section.label}
                  storageKey={groupConfig.storageKey}
                  routePrefix={groupConfig.routePrefix}
                  collapsed={collapsed}
                  accent={section.label === 'Admin'}
                >
                  {list}
                </SidebarGroup>
              );
            }
            return (
              <div key={section.label}>
                <SidebarSectionHeader
                  label={section.i18nKey ? t(section.i18nKey) : section.label}
                  collapsed={collapsed}
                  onToggleCollapse={sIdx === 0 ? toggleCollapse : undefined}
                  accent={section.label === 'Admin'}
                />
                {list}
              </div>
            );
          })}
        </nav>

        {/* The user mini-card + logout button used to live here — moved to the
            topbar avatar dropdown so there's exactly one sign-out control. */}
        <div className="mb-4" />
      </aside>
    </>
  );
}
